/**
 * Start buttons on the web (plan section 2b, "choose a program").
 *
 * Before a Start opens the Premium offer, the canonical server check decides:
 * - signed in: `user_has_program_access` (programs) or the content access
 *   status (Journeys, via hasActiveJourneyPurchase);
 * - signed out: `can_start_without_account(programId)`.
 * Only a denial saves the start intent and opens the offer. Any unknown
 * (a network error, or a server that predates the gate) keeps today's
 * behavior, because the session and Journey routes check access again.
 *
 * The routing helpers below read the saved intent back after signup, at
 * checkout and on PremiumWelcome, so the new member lands in what they chose.
 */
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { isMissingCoreAccessFunction } from '@/hooks/useCoreAccess';
import {
  readStartIntent,
  safeStartReturnPath,
  saveStartIntent,
  type StartIntent,
  type StartIntentKind,
} from '@/utils/startIntent';

/**
 * Signed-out Start for a program. `fallback` is today's answer (a free-tier
 * program starts, anything else shows the offer), used when the function is
 * not deployed yet or the call fails.
 */
export async function fetchCanStartWithoutAccount(programId: string, fallback: boolean): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('can_start_without_account', { p_program_id: programId });
    if (error) {
      if (!isMissingCoreAccessFunction(error)) {
        console.warn('can_start_without_account failed, keeping the default start:', error);
      }
      return fallback;
    }
    return typeof data === 'boolean' ? data : fallback;
  } catch (error) {
    console.warn('can_start_without_account failed, keeping the default start:', error);
    return fallback;
  }
}

/**
 * Signed-in Start for a program: the server's `user_has_program_access`.
 * Returns null when the server could not answer, so the caller starts as it
 * does today and the program route decides.
 */
export async function fetchProgramStartAccess(userId: string, programId: string): Promise<boolean | null> {
  try {
    const { data, error } = await supabase.rpc('user_has_program_access', {
      p_user_id: userId,
      p_program_id: programId,
    });
    if (error) {
      console.warn('user_has_program_access failed, keeping the default start:', error);
      return null;
    }
    return typeof data === 'boolean' ? data : null;
  } catch (error) {
    console.warn('user_has_program_access failed, keeping the default start:', error);
    return null;
  }
}

/**
 * Remembers what a web visitor chose before the offer. Native keeps
 * onboarding as its entry, so nothing is saved there.
 */
export function rememberStartChoice(input: {
  kind: StartIntentKind;
  id: string;
  returnPath: string;
  source: string;
}): void {
  if (Capacitor.isNativePlatform()) return;
  saveStartIntent(input);
}

/** The web offer page for a chosen program or Journey. */
export function startOfferPath(returnPath: string): string {
  return `/pricing?returnTo=${encodeURIComponent(safeStartReturnPath(returnPath))}`;
}

/** The program, session or Journey path a URL asks for, directly or through `returnTo`. */
function explicitStartPath(path: string | null | undefined): string | null {
  if (!path) return null;
  const direct = safeStartReturnPath(path, '');
  if (direct) return direct;
  try {
    const nested = new URL(path, 'https://journalparty.com').searchParams.get('returnTo');
    return nested ? safeStartReturnPath(nested, '') || null : null;
  } catch {
    return null;
  }
}

/** True when `path` leads to the intent's choice: its own path, or the offer for it. */
export function isStartIntentDestination(path: string | null | undefined, intent: StartIntent): boolean {
  return explicitStartPath(path) === intent.returnPath;
}

/**
 * Where signup or sign-in sends someone who chose a program before the
 * offer. Returns null when no fresh intent exists or an explicit redirect to
 * something else wins (callers keep their usual destination).
 *
 * - An explicit redirect to the chosen program or its offer is kept as is,
 *   skipping personalization onboarding.
 * - No explicit redirect: a new account goes back to the offer; a sign-in
 *   goes to the program, whose own check lets members straight in.
 */
export function resolveStartIntentAuthDestination({
  redirectPath,
  hasExplicitRedirect,
  isNewAccount,
}: {
  redirectPath: string;
  hasExplicitRedirect: boolean;
  isNewAccount: boolean;
}): string | null {
  if (Capacitor.isNativePlatform()) return null;
  const intent = readStartIntent();
  if (!intent) return null;
  if (hasExplicitRedirect) {
    return isStartIntentDestination(redirectPath, intent) ? redirectPath : null;
  }
  return isNewAccount ? startOfferPath(intent.returnPath) : intent.returnPath;
}

/**
 * Where web checkout success should return: the program, session or Journey
 * path the checkout itself names (validated, and kept even when storage is
 * unavailable or the signup continued in another browser), else the saved
 * intent. Null when neither exists.
 */
export function resolveStartIntentCheckoutReturn(returnPath: string | null | undefined): string | null {
  return explicitStartPath(returnPath) ?? readStartIntent()?.returnPath ?? null;
}
